import { InfoCategoryId } from "./model";

/**
 * The 24 virtual labels: one per information category and sensitivity level
 * (6 x 4), read from the labels the app already loads (`/labels/tree?category=
 * Virtual`, SharedState.virtualLabels) rather than hard-coded. Each carries its
 * category's L0 id (`infoTypeCategoryId`) and its level.
 *
 * They are cumulative, as their names say (checked on QA, 2026-09-29): a
 * highly sensitive file carries "Highly sensitive", "Medium+" and "Low+" of
 * its category, a medium one "Medium+" and "Low+", and "Non-sensitive" stands
 * alone. So a count per label is not a count per level — `ladder_to_levels`
 * turns one into the other — and a slider position is one label, not a sum:
 *
 *   High only -> Highly sensitive
 *   + Medium  -> Medium+
 *   + Low     -> Low+
 *   All       -> Low+ and Non-sensitive
 */

export type LevelKey = "high" | "medium" | "low" | "none";

export interface VirtualLabel {
  id: string;
  category: InfoCategoryId;
  level: LevelKey;
}

/** what the app's virtual-label records carry that matters here */
export interface VirtualLabelRecord {
  id: string;
  infoTypeCategoryId?: string;
  /** "High" | "Medium" | "Low" | "None" */
  sensitivityLevel?: string | number;
  /** 0.85 | 0.5 | 0.2 | 0 — read when the level is missing */
  sensitivity?: number;
  labelCategory?: string;
}

const LEVEL_BY_NAME: Record<string, LevelKey> = {
  high: "high",
  medium: "medium",
  low: "low",
  none: "none",
};

const level_of = (record: VirtualLabelRecord): LevelKey | undefined => {
  const named = LEVEL_BY_NAME[String(record.sensitivityLevel || "").toLowerCase()];
  if (named) return named;
  if (typeof record.sensitivity !== "number") return undefined;
  if (record.sensitivity >= 0.85) return "high";
  if (record.sensitivity >= 0.5) return "medium";
  if (record.sensitivity >= 0.2) return "low";
  return "none";
};

/**
 * Index the category x sensitivity virtual labels by id. MIP virtual labels
 * (`hasMipVirtual`) are a different family and are left out; so is any label
 * whose category is not one of the dashboard's six.
 *
 * `category_of` maps an L0 label id to its dashboard category. Per the spec,
 * the virtual labels' "Other" category is the UI's Personal, so the caller
 * resolves the Other L0 to "personal" as well.
 */
export const index_virtual_labels = (
  records: readonly VirtualLabelRecord[],
  category_of: (l0_id: string) => InfoCategoryId | undefined
): Map<string, VirtualLabel> => {
  const index = new Map<string, VirtualLabel>();
  records.forEach((record) => {
    if (!record || !record.id || record.labelCategory === "hasMipVirtual") return;
    const category = record.infoTypeCategoryId
      ? category_of(record.infoTypeCategoryId)
      : undefined;
    const level = level_of(record);
    if (category && level) index.set(record.id, { id: record.id, category, level });
  });
  return index;
};

/**
 * The virtual labels that select exactly the given levels of a category, for
 * a slider position (levels run down from "high"): the widest cumulative label
 * among them, plus Non-sensitive when it is included. Sending every label at
 * those levels instead would list a file once per label it carries.
 */
export const virtual_labels_for = (
  index: Map<string, VirtualLabel>,
  levels: readonly LevelKey[],
  category?: InfoCategoryId
): string[] => {
  const graded = (["high", "medium", "low"] as LevelKey[]).filter((l) => levels.includes(l));
  const wanted = new Set<LevelKey>([
    ...(graded.length ? [graded[graded.length - 1]] : []),
    ...(levels.includes("none") ? (["none"] as LevelKey[]) : []),
  ]);
  return [...index.values()]
    .filter((l) => wanted.has(l.level) && (!category || l.category === category))
    .map((l) => l.id);
};

/** counts on one category's four labels, as the server returns them */
export interface LabelLadder {
  /** "Highly sensitive" */
  high: number;
  /** "Medium+" — medium or higher */
  medium_plus: number;
  /** "Low+" — low or higher */
  low_plus: number;
  /** "Non-sensitive" */
  none: number;
}

export const empty_ladder = (): LabelLadder => ({ high: 0, medium_plus: 0, low_plus: 0, none: 0 });

/** add a label's count to its rung */
export const add_to_ladder = (ladder: LabelLadder, level: LevelKey, count: number) => {
  if (level === "high") ladder.high += count;
  else if (level === "medium") ladder.medium_plus += count;
  else if (level === "low") ladder.low_plus += count;
  else ladder.none += count;
};

/**
 * Cumulative label counts to a count per level. The rungs are clamped so a
 * lower rung is never below a higher one, which keeps every level at zero or
 * above if the server's counts ever disagree.
 */
export const ladder_to_levels = (ladder: LabelLadder) => {
  const medium_plus = Math.max(ladder.medium_plus, ladder.high);
  const low_plus = Math.max(ladder.low_plus, medium_plus);
  return {
    high: ladder.high,
    medium: medium_plus - ladder.high,
    low: low_plus - medium_plus,
    none: ladder.none,
  };
};
